/**
 * Loader Component
 * Reusable loading spinner and skeleton components
 */
import React from 'react';
import saccosLogo from '../../../assets/logos/saccos_logo.png';

/**
 * Loading Spinner Component
 * @param {Object} props - Component props
 * @param {string} props.size - Spinner size (sm, md, lg)
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Spinner element
 */
export const Spinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={`${sizeClasses[size]} ${className}`}>
      <svg
        className="animate-spin text-blue-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>
  );
};

/**
 * Page Loader Component
 * Full page branded loading indicator with SACCOS logo
 * @returns {JSX.Element} Page loader
 */
export const PageLoader = () => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white">
      <div className="relative w-24 h-24 md:w-32 md:h-32 mb-6">
        {/* Rotating gold ring */}
        <svg
          className="absolute inset-0 w-full h-full animate-spin"
          viewBox="0 0 100 100"
        >
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="#ca9a37"
            strokeWidth="3"
            strokeDasharray="70 180"
            strokeLinecap="round"
            opacity="0.8"
          />
        </svg>
        {/* Logo centered */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <img
            src={saccosLogo}
            alt="Uhamiaji SACCOS"
            className="w-12 h-12 md:w-16 md:h-16 object-contain animate-pulse"
          />
        </div>
      </div>
      <p className="text-[#2f3951] text-sm md:text-base font-medium tracking-wide animate-pulse">
        Loading...
      </p>
    </div>
  );
};

/**
 * Skeleton Loader Component
 * @param {Object} props - Component props
 * @param {string} props.type - Skeleton type (text, circle, rect)
 * @param {string} props.width - Width
 * @param {string} props.height - Height
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Skeleton element
 */
export const Skeleton = ({
  type = 'text',
  width = '100%',
  height = '1rem',
  className = '',
}) => {
  const baseClasses = 'animate-pulse bg-gray-200 dark:bg-gray-700 rounded';

  if (type === 'circle') {
    return (
      <div
        className={`${baseClasses} rounded-full ${className}`}
        style={{ width, height }}
      />
    );
  }

  if (type === 'rect') {
    return (
      <div
        className={`${baseClasses} ${className}`}
        style={{ width, height }}
      />
    );
  }

  return (
    <div
      className={`${baseClasses} ${className}`}
      style={{ width, height }}
    />
  );
};

/**
 * Card Skeleton Component
 * @returns {JSX.Element} Card skeleton
 */
export const CardSkeleton = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
      <Skeleton width="50%" height="1.5rem" className="mb-4" />
      <Skeleton width="100%" height="1rem" className="mb-2" />
      <Skeleton width="75%" height="1rem" />
    </div>
  );
};

/**
 * Table Skeleton Component
 * @param {Object} props - Component props
 * @param {number} props.rows - Number of skeleton rows
 * @returns {JSX.Element} Table skeleton
 */
export const TableSkeleton = ({ rows = 5 }) => {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex space-x-4 pb-4">
        <Skeleton width="25%" height="2rem" />
        <Skeleton width="25%" height="2rem" />
        <Skeleton width="25%" height="2rem" />
        <Skeleton width="25%" height="2rem" />
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex space-x-4">
          <Skeleton width="25%" height="3rem" />
          <Skeleton width="25%" height="3rem" />
          <Skeleton width="25%" height="3rem" />
          <Skeleton width="25%" height="3rem" />
        </div>
      ))}
    </div>
  );
};

export default { Spinner, PageLoader, Skeleton, CardSkeleton, TableSkeleton };
